// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { color, mergeWithCss } from "@stoneage/XDL/Utilities";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface MyAccountFilterButtonProps {
    extendedStyles?: MyAccountFilterButtonStyles;
    toggleFiltersOpen: () => void;
}

type Props = MyAccountFilterButtonProps;

interface MyAccountFilterButtonStyles {
    filterIcon?: IconPresentationProps;
    toggleIcon?: IconPresentationProps;
    toggleFilterClickable?: ButtonPresentationProps;
    toggleFilterTypography: TypographyPresentationProps;
}

const myAccountFilterButtonStyles: MyAccountFilterButtonStyles = {
    filterIcon: {
        src: "IconFilters",
        size: 24,
    },
    toggleIcon: {
        size: 4,
        css: css`
            &&& {
                margin: 0;
                height: 8px;
                width: 8px;
                & path :hover {
                    fill: ${color("primary")};
                }
            }
        `,
    },
    toggleFilterClickable: {
        mergeCss: true,
        icon: {
            src: "",
            position: "right",
        },
        css: css`
            padding: 8px 25px;
        `,
    },
    toggleFilterTypography: {
        css: css`
            margin: 0 8px;
        `,
    },
};

const MyAccountFilterButton: FC<Props> = ({ extendedStyles, toggleFiltersOpen }) => {
    const [styles] = useState(() => mergeWithCss(myAccountFilterButtonStyles, extendedStyles));
    const [isFilterOpened, setIsFilterOpened] = useState(false);
    return (
        <Button
            {...styles.toggleFilterClickable}
            onClick={() => {
                toggleFiltersOpen();
                setIsFilterOpened(value => !value);
            }}
            data-test-selector="orderHistory_showHideFilters"
        >
            <Icon {...styles.filterIcon} />
            <Typography {...styles.toggleFilterTypography}>{translate("Filter Results")}</Typography>
            {isFilterOpened && <Icon src="IconArrowSmallWhiteUp" {...styles.toggleIcon} />}
            {!isFilterOpened && <Icon src="IconArrowSmallWhiteDown" {...styles.toggleIcon} />}
        </Button>
    );
};

export default MyAccountFilterButton;
